import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { BACKDOOR_TOKEN_KEY } from '@/shared/constants/auth';
import { detectIframe, requestTokenFromParent } from '@/shared/utils/parentAppBridge';

/** Extends InternalAxiosRequestConfig to track single-retry attempts. */
interface RetryableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

class ApiClient {
  private readonly client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    console.log(`API Client initialized with base URL: ${API_BASE_URL}`);
    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor - Add auth token
    this.client.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        const token = await this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - Handle errors
    this.client.interceptors.response.use(
      response => response,
      async error => {
        const config = error.config as RetryableConfig | undefined;
        if (error.response?.status === 401 && !config?._retried && detectIframe()) {
          // Inside iframe: ask the parent app for a fresh token, then retry once.
          try {
            const newToken = await requestTokenFromParent();
            sessionStorage.setItem(BACKDOOR_TOKEN_KEY, newToken);
            localStorage.setItem(BACKDOOR_TOKEN_KEY, newToken);
            if (config) {
              config._retried = true;
              config.headers.Authorization = `Bearer ${newToken}`;
              return this.client.request(config);
            }
          } catch (refreshError) {
            console.error('Token refresh via parent app failed:', refreshError);
          }
        }
        if (error.response?.status === 401) {
          await this.handleUnauthorized();
        }
        throw error;
      }
    );
  }

  private async getAuthToken(): Promise<string | null> {
    try {
      // Check for stored token (sessionStorage first, then localStorage for PWA)
      if (globalThis.window !== undefined) {
        const storedToken =
          sessionStorage.getItem(BACKDOOR_TOKEN_KEY) || localStorage.getItem(BACKDOOR_TOKEN_KEY);
        if (storedToken) {
          return storedToken;
        }
      }

      return null;
    } catch (error) {
      console.error('Failed to acquire token:', error);
      return null;
    }
  }

  public async handleUnauthorized(): Promise<void> {
    try {
      // Clear backdoor tokens from both storages
      sessionStorage.removeItem(BACKDOOR_TOKEN_KEY);
      localStorage.removeItem(BACKDOOR_TOKEN_KEY);

      // Redirect to error page
      window.location.href = '/error';
    } catch (error) {
      console.error('Error handling unauthorized:', error);
    }
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(url, config);
    return response.data;
  }

  public async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(url, data, config);
    return response.data;
  }

  public async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.put(url, data, config);
    return response.data;
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(url, config);
    return response.data;
  }

  public async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.patch(url, data, config);
    return response.data;
  }
}

export const apiClient = new ApiClient();
export default apiClient;
